import "./style.css";
import api from "../../services/api";
import { confirm } from "../confirm";
import Ajudinha from "../ajudinha";
import { useAppSelector } from "../../store/hooks";
import { CircularProgress } from "@mui/material";
import { SkeletonLoader } from "./SkeletonLoader";
import { Modal } from "react-bootstrap";
import { obterIdTenent } from "../../services/seguranca";
import { corMaisClara } from "../../services/utilitarios";
import { TipoFormulario } from "../../services/constantes";
import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { FaTimes, FaRobot } from "react-icons/fa";
import { FaDownload } from "react-icons/fa";
import ModalAIChat, { AITriggerButton } from "../modal-ai-chat";
import { Campo } from "../../services/tipos";

import { AvisoGuia, DicaGuia, PropsModalForm } from "./types";
import BalaoGuia from "./BalaoGuia";
import { useFormState } from "./hooks/useFormState";
import { useFileUpload } from "./hooks/useFileUpload";
import { getFieldRenderer } from "./field-renderers";
import {
  StyledModal,
  ModalContentWrapper,
  CartaoEmbutido,
  StyledModalHeader,
  ModalTitleContainer,
  ModalTitle,
  HeaderActions,
  CloseHeaderButton,
  TutorialIcon,
  StyledModalBody,
  PassosCompactos,
  PassoCompacto,
  SeparadorPasso,
  StyledForm,
  StyledRow,
  StyledCol,
  StyledFormGroup,
  StyledLabel,
  CampoContainer,
  AjudinhaWrapper,
  NovoIndicator,
  ExtraButton,
  StyledModalFooter,
  FooterButtonsContainer,
  FooterTextContainer,
  FooterSlot,
  FooterText,
  KeyboardHint,
  PrimaryButton,
  SecondaryButton,
  LoadingOverlay,
  ImagePreviewOverlay,
  ImagePreviewContent,
  PreviewImage,
  CloseButton,
  DownloadButton,
  ProgressContainer,
  ProgressStep,
  StepCircle,
  StepTitle,
  TutorialVideoOverlay,
  TutorialVideoContainer,
  TutorialVideoHeader,
  TutorialVideoTitle,
  TutorialVideoClose,
  TutorialVideoBody,
  TutorialVideoIframe,
  TutorialVideoExternalLink,
  TutorialVideoHeaderActions,
} from "./styles";

// Re-export types for backward compatibility
export type { PropsModalForm } from "./types";

const HelpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 19H11V17H13V19ZM15.07 11.25L14.17 12.17C13.45 12.9 13 13.5 13 15H11V14.5C11 13.4 11.45 12.4 12.17 11.67L13.41 10.41C13.78 10.05 14 9.55 14 9C14 7.9 13.1 7 12 7C10.9 7 10 7.9 10 9H8C8 6.79 9.79 5 12 5C14.21 5 16 6.79 16 9C16 9.88 15.64 10.68 15.07 11.25Z" fill="currentColor" />
  </svg>
);

const toYoutubeEmbedUrl = (url: string): string => {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    if (u.hostname.includes('youtube.com')) {
      if (u.pathname.startsWith('/embed/')) return url;
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
  } catch {
    // fallback abaixo
  }
  return url;
};

const ModalForm: React.FC<PropsModalForm> = (props) => {
  const [token] = useState(useAppSelector((state) => state.token));
  const [isSaving, setIsSaving] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const formState = useFormState({
    show: props.show,
    campos: props.campos,
    modo: props.modo,
    entidadeSelecionada: props.entidadeSelecionada,
    atualizacaoEntidade: props.atualizacaoEntidade,
    pages: props.pages,
    pageIndex: props.pageIndex,
    currentPageIndex: props.currentPageIndex,
  });

  const fileUpload = useFileUpload({
    show: props.show,
    campos: props.campos,
    entidadeSelecionada: props.entidadeSelecionada,
    entidade: formState.entidade,
    setEntidade: formState.setEntidade,
    setErrors: formState.setErrors,
    onImageRemoved: props.onImageRemoved,
    onFileRemoved: props.onFileRemoved,
    onVideoRemoved: props.onVideoRemoved,
  });

  // Auto-focus first field
  useEffect(() => {
    if (props.show && !props.carregando) {
      const timer = setTimeout(() => {
        const firstInput = formRef.current?.querySelector<HTMLInputElement>(
          'input:not([type="hidden"]):not([readonly]):not([disabled]), select:not([disabled]), textarea:not([readonly]):not([disabled])'
        );
        firstInput?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [props.show, props.carregando]);

  // Ctrl+Enter shortcut
  useEffect(() => {
    if (!props.show) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (props.modo !== TipoFormulario.tpVisualizacao) {
          formRef.current?.requestSubmit();
        }
      }
      // Esc
      if (e.key === 'Escape') {
        if (fileUpload.showImageModal) {
          fileUpload.setShowImageModal(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [props.show, props.modo, fileUpload.showImageModal]);

  const [tutorialVideoUrl, setTutorialVideoUrl] = useState<string | null>(null);

  const handleTutorialClick = useCallback((link: string) => {
    setTutorialVideoUrl(link);
  }, []);

  // ===================== SUBMIT =====================

  const scrollToFirstError = useCallback(() => {
    setTimeout(() => {
      if (!formRef.current) return;
      const allDivs = formRef.current.querySelectorAll('div');
      for (const div of Array.from(allDivs)) {
        const computed = window.getComputedStyle(div);
        if (computed.color === 'rgb(220, 53, 69)' && div.textContent?.includes('obrigatório')) {
          div.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
    }, 100);
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    // Impede que o submit de um ModalForm aninhado (ex: modal de regra de comissão
    // dentro do cadastro de usuário) borbulhe pela árvore do React via portal e dispare
    // o submit do form externo, avançando a etapa indevidamente.
    e.stopPropagation();
    if (isSaving) return;

    if (props.pages) {
      if (!formState.validateCurrentPage()) {
        scrollToFirstError();
        return;
      }
      if (formState.currentPage < props.pages.length - 1) {
        formState.setCurrentPage(formState.currentPage + 1);
        return;
      }
    } else {
      if (!formState.validateForm()) {
        scrollToFirstError();
        return;
      }
    }

    setIsSaving(true);
    const entidadeAtualizada = { ...formState.entidade };
    props.campos.forEach((campo: Campo) => {
      entidadeAtualizada[campo.nome] = formState.getFieldValue(campo.nome);
    });

    if (props.onSave) {
      props.onSave(entidadeAtualizada);
      setIsSaving(false);
    } else {
      handleSave(entidadeAtualizada);
    }
  }, [isSaving, formState.entidade, props.campos, props.onSave, formState.getFieldValue,
    formState.validateForm, props.pages, formState.currentPage, formState.validateCurrentPage]);

  const handleSave = useCallback(async (entidade: any) => {
    try {
      const usarFormData = props.campos?.some(
        (campo: Campo) => campo.tipo === "image" || campo.tipo === "file" || campo.tipo === "video"
      );
      const dadosASalvar = usarFormData ? new FormData() : ({} as any);

      for (const key in entidade) {
        if (Object.prototype.hasOwnProperty.call(entidade, key)) {
          let value = entidade[key];
          const campoEncontrado = props.campos.find((campo: Campo) => campo.nome === key);

          if (typeof value === "string" && value.trim() === "") {
            value = null;
          }
          if (campoEncontrado?.tipo === "checkbox") {
            value = value !== null ? value : false;
          }
          if (campoEncontrado?.tipo === "decimal" || campoEncontrado?.tipo === "money") {
            value = value || 0;
          }

          if (key !== "id") {
            if (usarFormData) {
              dadosASalvar.append(key, value);
            } else {
              dadosASalvar[key] = value;
            }
          }

          if (props.modo === TipoFormulario.tpEdicao) {
            if (usarFormData) {
              dadosASalvar.append("id", entidade["id"]);
            } else {
              dadosASalvar["id"] = entidade["id"];
            }
          }

          if (props.apiConfig.idTenentCampo) {
            const idTenent = obterIdTenent(token);
            if (usarFormData) {
              dadosASalvar.append(props.apiConfig.idTenentCampo, idTenent);
            } else {
              dadosASalvar[props.apiConfig.idTenentCampo] = idTenent;
            }
          }
        }
      }

      const apiAcao = props.modo === TipoFormulario.tpCadastro ? api.post : api.put;
      const response = await apiAcao(`/${props.apiConfig.endpoint}/`, dadosASalvar, {
        headers: usarFormData
          ? { "Content-Type": "multipart/form-data" }
          : { "Content-Type": "application/json" },
      });

      props.apiConfig.requisicaoBemSucedida(response);
    } catch (error: any) {
      props.apiConfig.tratarErro(error);
    } finally {
      setIsSaving(false);
    }
  }, [props.modo, props.apiConfig, props.campos, token]);

  // ===================== CLOSE / CANCEL =====================

  const handleClose = useCallback(() => {
    props.onHide();
    formState.resetForm();
    fileUpload.resetPreviews();
    setIsSaving(false);
    setShowAIChat(false);
  }, [props.onHide, formState.resetForm, fileUpload.resetPreviews]);

  const handleCancel = useCallback(async () => {
    const shouldCancel = await confirm("Deseja realmente cancelar?");
    if (shouldCancel) {
      handleClose();
      props.onCancel();
    }
  }, [handleClose, props.onCancel]);

  const handlePreviousPage = useCallback(() => {
    if (formState.currentPage > 0) {
      formState.setCurrentPage(formState.currentPage - 1);
    }
  }, [formState.currentPage, formState.setCurrentPage]);

  // ===================== RENDER FIELDS =====================

  const renderCamposPorLinha = useCallback(() => {
    const pageCampos = props.pages?.[formState.currentPage]?.campos;

    const camposFiltrados = pageCampos
      ? props.campos.filter(campo => {
        const isInPage = pageCampos.includes(campo.nome);
        const isVisible = typeof campo.visible === 'function'
          ? campo.visible(formState.entidade)
          : campo.visible !== false;
        return isInPage && isVisible;
      })
      : props.campos.filter(campo => {
        const isVisible = typeof campo.visible === 'function'
          ? campo.visible(formState.entidade)
          : campo.visible !== false;
        return isVisible;
      });

    const camposAgrupadosPorLinha = camposFiltrados.reduce(
      (acc: Record<number, Campo[]>, campo: Campo) => {
        if (!acc[campo.linha]) acc[campo.linha] = [];
        acc[campo.linha].push(campo);
        return acc;
      },
      {}
    );

    return Object.keys(camposAgrupadosPorLinha).map((linha, index) => (
      <StyledRow key={index}>
        {/* Number e não parseInt: com `linha: 94.5` o parseInt vira 94, a busca cai no grupo de
            outra linha e os campos daquela linha somem da tela sem erro nenhum. */}
        {camposAgrupadosPorLinha[Number(linha)].map((campo: Campo) => {
          const FieldRenderer = getFieldRenderer(campo.tipo, !!campo.mascara);
          const isLabel = campo.tipo === "label";

          return (
            <StyledCol key={campo.nome} md={campo.largura} xs={12}>
              {isLabel ? (
                <FieldRenderer
                  campo={campo}
                  modo={props.modo}
                  entidade={formState.entidade}
                  getFieldValue={formState.getFieldValue}
                  handleInputChange={formState.handleInputChange}
                  handleBlur={formState.handleBlur}
                  errors={formState.errors}
                />
              ) : (
                <StyledFormGroup
                  controlId={campo.nome}
                  data-tour={`campo-${campo.nome}`}
                  $hasError={!!formState.errors[campo.nome]}
                >
                  {campo.tipo !== 'checkbox' && campo.tipo !== 'toggle' && (campo.label || campo.ajudinha?.texto) && (
                    <CampoContainer>
                      <StyledLabel required={campo.required}>
                        {campo.label}
                        {campo.novo && <NovoIndicator>NOVO</NovoIndicator>}
                      </StyledLabel>
                      {campo.ajudinha?.texto && (
                        <AjudinhaWrapper>
                          <Ajudinha
                            texto={campo.ajudinha.texto}
                            autoCloseTime={campo.ajudinha.autoCloseTime}
                          />
                        </AjudinhaWrapper>
                      )}
                      {campo.tutorialLink && (
                        <TutorialIcon
                          type="button"
                          title="Assistir tutorial"
                          onClick={() => handleTutorialClick(campo.tutorialLink!)}
                        >
                          <HelpIcon />
                          Entenda como Funciona
                        </TutorialIcon>
                      )}
                      {campo.botaoExtra && campo.botaoExtra.link && (
                        <ExtraButton
                          type="button"
                          title={campo.botaoExtra.tooltip}
                          onClick={() =>
                            window.open(campo.botaoExtra?.link ?? "", '_blank', 'noopener,noreferrer')
                          }
                        >
                          <campo.botaoExtra.icone />
                        </ExtraButton>
                      )}
                    </CampoContainer>
                  )}

                  <FieldRenderer
                    campo={campo}
                    modo={props.modo}
                    entidade={formState.entidade}
                    getFieldValue={formState.getFieldValue}
                    handleInputChange={formState.handleInputChange}
                    handleBlur={formState.handleBlur}
                    errors={formState.errors}
                    planoPremium={props.planoPremium}
                    imagePreview={fileUpload.imagePreview}
                    filePreview={fileUpload.filePreview}
                    videoPreview={fileUpload.videoPreview}
                    dragActive={fileUpload.dragActive}
                    handleImageChange={fileUpload.handleImageChange}
                    handleFileChange={fileUpload.handleFileChange}
                    handleVideoChange={fileUpload.handleVideoChange}
                    handleImageRemove={fileUpload.handleImageRemove}
                    handleFileRemove={fileUpload.handleFileRemove}
                    handleVideoRemove={fileUpload.handleVideoRemove}
                    handleDrag={fileUpload.handleDrag}
                    handleDrop={fileUpload.handleDrop}
                    handleDownloadFile={fileUpload.handleDownloadFile}
                    handleViewImage={fileUpload.handleViewImage}
                    showImageModal={fileUpload.showImageModal}
                    videoRecording={fileUpload.videoRecording}
                    currentVideoField={fileUpload.currentVideoField}
                    startVideoRecording={fileUpload.startVideoRecording}
                    stopVideoRecording={fileUpload.stopVideoRecording}
                    cancelVideoRecording={fileUpload.cancelVideoRecording}
                  />
                </StyledFormGroup>
              )}
            </StyledCol>
          );
        })}
      </StyledRow>
    ));
  }, [
    props.campos, props.modo, props.planoPremium, props.pages,
    formState.entidade, formState.currentPage, formState.getFieldValue,
    formState.handleInputChange, formState.handleBlur, formState.errors,
    fileUpload.imagePreview, fileUpload.filePreview, fileUpload.videoPreview,
    fileUpload.dragActive, fileUpload.videoRecording, fileUpload.currentVideoField,
    handleTutorialClick,
  ]);

  // ===================== PROGRESS STEPS =====================

  const renderProgressSteps = useCallback(() => {
    if (!props.pages) return null;

    if (props.corpoAlto) {
      return (
        <PassosCompactos>
          {props.pages.map((page, index) => {
            const ativo = index === formState.currentPage;
            const feito = index < formState.currentPage;
            return (
              <React.Fragment key={index}>
                {index > 0 && <SeparadorPasso completed={index <= formState.currentPage} />}
                <PassoCompacto active={ativo} completed={feito} aria-current={ativo ? 'step' : undefined}>
                  <span>{feito ? '\u2713' : index + 1}</span>
                  {page.titulo}
                </PassoCompacto>
              </React.Fragment>
            );
          })}
        </PassosCompactos>
      );
    }

    return (
      <ProgressContainer>
        {props.pages.map((page, index) => (
          <ProgressStep
            key={index}
            active={index === formState.currentPage}
            completed={index < formState.currentPage}
          >
            <StepCircle
              active={index === formState.currentPage}
              completed={index < formState.currentPage}
            >
              {index < formState.currentPage ? '' : index + 1}
            </StepCircle>
            <StepTitle
              active={index === formState.currentPage}
              completed={index < formState.currentPage}
            >
              {page.titulo}
            </StepTitle>
          </ProgressStep>
        ))}
      </ProgressContainer>
    );
  }, [props.pages, props.corpoAlto, formState.currentPage]);

  // ===================== FOOTER BUTTONS =====================

  const renderFooterButtons = () => {
    const isVisualizacao = props.modo === TipoFormulario.tpVisualizacao;
    const isLastPage = !props.pages || formState.currentPage >= props.pages.length - 1;
    const hasPages = !!props.pages;
    const saving = isSaving || props.isSaving;

    const submitLabel = hasPages && !isLastPage
      ? "Avançar"
      : (props.tituloBotaoOk || "Salvar");

    return (
      <FooterButtonsContainer>
        {props.embutido ? null : isVisualizacao ? (
          <SecondaryButton variant="secondary" onClick={handleClose}>
            Fechar
          </SecondaryButton>
        ) : (
          <SecondaryButton variant="secondary" onClick={handleCancel}>
            Cancelar
          </SecondaryButton>
        )}

        {hasPages && formState.currentPage > 0 && (
          <SecondaryButton variant="secondary" onClick={handlePreviousPage}>
            Voltar
          </SecondaryButton>
        )}

        {props.botaoExtra?.titulo && (!hasPages || isLastPage) && (
          <PrimaryButton
            hoverBackground={props.botaoExtra?.cor && corMaisClara(props.botaoExtra.cor, 20)}
            background={props.botaoExtra?.cor}
            onClick={() => props.botaoExtra?.onClick(props.entidadeSelecionada)}
            disabled={saving}
          >
            {props.botaoExtra.titulo}
          </PrimaryButton>
        )}

        {props.botoesExtras?.map((botao, idx) => (
          <PrimaryButton
            key={idx}
            hoverBackground={botao.cor && corMaisClara(botao.cor, 20)}
            background={botao.cor}
            onClick={() => botao.onClick(props.entidadeSelecionada)}
            disabled={saving}
          >
            {botao.titulo}
          </PrimaryButton>
        ))}

        <PrimaryButton
          data-tour={hasPages && !isLastPage ? "btn-avancar" : "btn-salvar"}
          hoverBackground={props.corBotaoOk && corMaisClara(props.corBotaoOk, 20)}
          background={props.corBotaoOk}
          type="submit"
          variant="primary"
          disabled={saving || (isVisualizacao && isLastPage && !props.habilitarBotaoOkEmVisualizacao)}
          isSaving={saving}
        >
          {submitLabel}
        </PrimaryButton>

        {!isVisualizacao && (
          <KeyboardHint>
            <kbd>Ctrl</kbd>+<kbd>Enter</kbd>
          </KeyboardHint>
        )}
      </FooterButtonsContainer>
    );
  };

  // ===================== CRIAÇÃO GUIADA =====================

  // Avisos do guia já dispensados neste preenchimento ("página|campo").
  const [avisosVistos, setAvisosVistos] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (props.show) setAvisosVistos(new Set());
  }, [props.show]);

  // Valor de cada campo com aviso quando o aviso apareceu ("página|campo"): mexer no campo dispensa
  // o aviso, inclusive nos que já vêm preenchidos (o prazo mínimo nasce com 1 mês).
  const valorInicialDosAvisos = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    if (props.show) valorInicialDosAvisos.current = new Map();
  }, [props.show]);

  // Campo em que o cursor está. Enquanto a pessoa digita, o balão espera ela sair do campo, senão
  // pulava para o próximo já na primeira letra. Listas, toggles e checkboxes não esperam: escolher
  // já é terminar.
  const [campoEmEdicao, setCampoEmEdicao] = useState<string | null>(null);
  // Campos em que a pessoa já mexeu (clicou, escolheu ou passou por eles). Um aviso sai quando ela
  // mexe no campo, mesmo que o valor fique igual (caução em 0, "Juros por dia" que já vinha marcado).
  const [camposTocados, setCamposTocados] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (props.show) setCamposTocados(new Set());
  }, [props.show]);
  useEffect(() => {
    if (!props.guia || !props.show) return;
    const nomeDoCampo = (el: EventTarget | Element | null) => {
      const alvo = (el as HTMLElement | null)?.closest?.('[data-tour^="campo-"]');
      return alvo ? alvo.getAttribute('data-tour')!.slice('campo-'.length) : null;
    };
    const tocar = (nome: string | null) => {
      if (nome) setCamposTocados(prev => prev.has(nome) ? prev : new Set(prev).add(nome));
    };
    const aoEntrar = () => setCampoEmEdicao(nomeDoCampo(document.activeElement));
    const aoSair = (e: FocusEvent) => {
      tocar(nomeDoCampo(e.target));
      window.setTimeout(() => setCampoEmEdicao(nomeDoCampo(document.activeElement)), 0);
    };
    const aoClicar = (e: Event) => tocar(nomeDoCampo(e.target));
    document.addEventListener('focusin', aoEntrar);
    document.addEventListener('focusout', aoSair);
    document.addEventListener('click', aoClicar, true);
    return () => {
      document.removeEventListener('focusin', aoEntrar);
      document.removeEventListener('focusout', aoSair);
      document.removeEventListener('click', aoClicar, true);
    };
  }, [props.guia, props.show]);

  const passoDoGuia = useMemo((): (DicaGuia & { seletor: string; chave: string; aviso?: AvisoGuia }) | null => {
    const guia = props.guia;
    if (!guia || !props.show || props.carregando || props.modo !== TipoFormulario.tpCadastro) return null;

    const paginas = props.pages;
    const pagina = paginas?.[formState.currentPage];
    const tituloPagina = pagina?.titulo ?? "";
    const entidade = formState.entidade;
    const naPagina = (nome: string) => !pagina || pagina.campos.includes(nome);
    const visivel = (c: Campo) => typeof c.visible === "function" ? c.visible(entidade) : c.visible !== false;
    const TIPOS_SEM_ESPERA = ["select", "toggle", "checkbox", "multiselect", "selectgrid", "custom"];
    const digitando = (c: Campo) => campoEmEdicao === c.nome && !TIPOS_SEM_ESPERA.includes(c.tipo);
    // Vazio, zero e desmarcado contam como o mesmo "nada", para um valor padrão que chega depois
    // (0,00 no dinheiro) não parecer mexida da pessoa.
    const assinatura = (nome: string) => {
      const v = formState.getFieldValue(nome);
      if (v === "" || v === null || v === undefined || v === false || v === "INVALID_DATE") return "";
      // O toggle grava o próprio padrão ao aparecer: continuar nele não é mexida.
      const campo = props.campos.find(c => c.nome === nome);
      if (campo?.tipo === "toggle" && campo.defaultValueToggle !== undefined && String(v) === String(campo.defaultValueToggle)) return "";
      if (typeof v !== "object" && Number(v) === 0) return "";
      return JSON.stringify(v);
    };
    const vazio = (c: Campo) => {
      const v = formState.getFieldValue(c.nome);
      if (v === "" || v === null || v === undefined || v === "INVALID_DATE") return true;
      if ((c.tipo === "money" || c.tipo === "decimal") && !Number(v)) return true;
      return typeof v === "object" && !Array.isArray(v) && "value" in v && (v.value === "" || v.value === null || v.value === undefined);
    };

    const posicao = (nome: string) => {
      const c = props.campos.find(x => x.nome === nome);
      return c ? Number(c.linha) * 100 + Number(c.coluna) : Number.MAX_SAFE_INTEGER;
    };
    const pendentes: Array<{ posicao: number; passo: DicaGuia & { seletor: string; chave: string; aviso?: AvisoGuia } }> = [];

    (guia.avisos?.(tituloPagina, entidade) ?? [])
      // O aviso sai com o "Entendi" ou quando a pessoa mexe no próprio campo (marca o checkbox,
      // informa a caução), depois que ela termina de digitar.
      .filter(a => {
        const campo = props.campos.find(c => c.nome === a.alvo);
        const chave = `${tituloPagina}|${a.alvo}`;
        if (!naPagina(a.alvo) || (campo && !visivel(campo)) || avisosVistos.has(chave)) return false;
        const agora = assinatura(a.alvo);
        if (!valorInicialDosAvisos.current.has(chave)) valorInicialDosAvisos.current.set(chave, agora);
        if (campo && digitando(campo)) return true;
        return valorInicialDosAvisos.current.get(chave) === agora && !camposTocados.has(a.alvo);
      })
      .forEach(a => pendentes.push({
        posicao: posicao(a.alvo),
        passo: { ...a, seletor: `[data-tour="campo-${a.alvo}"]`, chave: `aviso|${tituloPagina}|${a.alvo}`, aviso: a },
      }));

    props.campos
      .filter(c => c.required && c.tipo !== "label" && c.nome && naPagina(c.nome) && visivel(c) && !c.apenasLeitura && (vazio(c) || digitando(c)))
      .forEach(c => pendentes.push({
        posicao: posicao(c.nome),
        passo: {
          ...(guia.dicas?.[c.nome] ?? { titulo: String(c.label || "Campo obrigatório"), texto: "Preencha este campo para continuar." }),
          seletor: `[data-tour="campo-${c.nome}"]`,
          chave: `campo|${tituloPagina}|${c.nome}`,
        },
      }));

    // Na mesma posição, o aviso vem antes do campo (ex.: "preencha pela CNH" antes do nome).
    pendentes.sort((a, b) => a.posicao - b.posicao || Number(!!b.passo.aviso) - Number(!!a.passo.aviso));
    if (pendentes.length > 0) return pendentes[0].passo;

    const ultima = !paginas || formState.currentPage >= paginas.length - 1;
    return {
      ...guia.fimDaPagina(tituloPagina, entidade, ultima),
      seletor: `[data-tour="${ultima ? "btn-salvar" : "btn-avancar"}"]`,
      chave: `fim|${tituloPagina}`,
    };
  }, [props.guia, props.show, props.carregando, props.modo, props.pages, props.campos,
    formState.currentPage, formState.entidade, formState.getFieldValue, avisosVistos, campoEmEdicao, camposTocados]);

  const dispensarAvisoDoGuia = useCallback(() => {
    const aviso = passoDoGuia?.aviso;
    if (!aviso) return;
    const tituloPagina = props.pages?.[formState.currentPage]?.titulo ?? "";
    setAvisosVistos(prev => new Set(prev).add(`${tituloPagina}|${aviso.alvo}`));
  }, [passoDoGuia, props.pages, formState.currentPage]);

  // Avisa quem usa o guia de cada balão novo exibido (uma vez por balão, por montagem).
  const passosExibidos = useRef<Set<string>>(new Set());
  const balaoVisivel = Boolean(passoDoGuia && !isSaving && !props.isSaving);
  useEffect(() => {
    if (!balaoVisivel || !passoDoGuia || passosExibidos.current.has(passoDoGuia.chave)) return;
    passosExibidos.current.add(passoDoGuia.chave);
    try {
      props.guia?.aoExibirPasso?.({ chave: passoDoGuia.chave, titulo: passoDoGuia.titulo, ordem: passosExibidos.current.size });
    } catch { /* quem observa o guia nunca quebra o formulário */ }
  }, [balaoVisivel, passoDoGuia?.chave]);

  // Acoplado à página não há onShow do Modal: avisa quando passa a aparecer.
  useEffect(() => {
    if (props.embutido && props.show) props.onShow?.();
  }, [props.embutido, props.show]);

  const conteudo = props.carregando ? (
      <SkeletonLoader rows={4} />
    ) : (
      <>
        {(isSaving || props.isSaving) && (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        )}

        <StyledForm ref={formRef} onSubmit={handleSubmit} $alto={props.corpoAlto}>
          {/* HEADER */}
          <StyledModalHeader>
            <ModalTitleContainer>
              <ModalTitle>
                {props.titulo ? props.titulo : props.modo}
              </ModalTitle>
              {props.ajudinha?.texto && (
                <Ajudinha
                  texto={props.ajudinha.texto}
                  autoCloseTime={props.ajudinha.autoCloseTime}
                  ehDestacado={props.ajudinha.ehDestacado}
                />
              )}
            </ModalTitleContainer>

            <HeaderActions>
              <AITriggerButton
                type="button"
                title="Pergunte à IA"
                onClick={() => setShowAIChat(!showAIChat)}
              >
                <FaRobot size={13} />
                Pergunte à IA
              </AITriggerButton>
              {props.urlTutorial && (
                <TutorialIcon
                  type="button"
                  title="Entenda como funciona"
                  onClick={() => handleTutorialClick(props.urlTutorial!)}
                >
                  <HelpIcon />
                  Entenda como Funciona
                </TutorialIcon>
              )}
              {!props.embutido && <CloseHeaderButton
                type="button"
                onClick={
                  props.modo === TipoFormulario.tpEdicao || props.modo === TipoFormulario.tpCadastro
                    ? handleCancel
                    : handleClose
                }
                title="Fechar"
                aria-label="Fechar modal"
              >
                <FaTimes />
              </CloseHeaderButton>}
            </HeaderActions>
          </StyledModalHeader>

          {/* PROGRESS STEPS */}
          {renderProgressSteps()}

          {/* BODY */}
          <StyledModalBody $alto={props.corpoAlto}>
            {renderCamposPorLinha()}
          </StyledModalBody>

          {/* FOOTER */}
          <StyledModalFooter>
            {props.footerContent && (
              <FooterSlot>{props.footerContent}</FooterSlot>
            )}
            {props.footerText && props.footerText.length > 0 && (
              <FooterTextContainer>
                {props.footerText.map((item, index) => (
                  <FooterText key={index} color={item.color} bold={item.bold}>
                    {item.text}
                  </FooterText>
                ))}
              </FooterTextContainer>
            )}
            {renderFooterButtons()}
          </StyledModalFooter>
        </StyledForm>
      </>
    );

  // ===================== RENDER =====================

  return (
    <>
      {passoDoGuia && !isSaving && !props.isSaving && (
        <BalaoGuia
          seletor={passoDoGuia.seletor}
          chave={passoDoGuia.chave}
          titulo={passoDoGuia.titulo}
          texto={passoDoGuia.texto}
          botao={passoDoGuia.aviso ? (passoDoGuia.aviso.botao ?? "Entendi") : undefined}
          aoConfirmar={passoDoGuia.aviso ? dispensarAvisoDoGuia : undefined}
        />
      )}
      {props.embutido ? (
        props.show && <CartaoEmbutido data-guia-container>{conteudo}</CartaoEmbutido>
      ) : (
      <StyledModal
        show={props.show}
        onHide={handleClose}
        size={props.tamanho}
        onShow={props.onShow}
        enforceFocus={!showAIChat}
        backdrop={
          props.modo === TipoFormulario.tpEdicao || props.modo === TipoFormulario.tpCadastro
            ? "static"
            : true
        }
        keyboard={
          !(props.modo === TipoFormulario.tpEdicao || props.modo === TipoFormulario.tpCadastro)
        }
        animation={false}
      >
        <ModalContentWrapper>
          {conteudo}
        </ModalContentWrapper>
      </StyledModal>
      )}

      {showAIChat && (
        <ModalAIChat
          contexto={props.titulo || 'Formulário'}
          onClose={() => setShowAIChat(false)}
        />
      )}

      {/* Image Preview Overlay */}
      {fileUpload.showImageModal && (
        <ImagePreviewOverlay onClick={() => fileUpload.setShowImageModal(false)}>
          <ImagePreviewContent onClick={(e) => e.stopPropagation()}>
            <PreviewImage src={fileUpload.currentImage} alt="Visualização" />
            <CloseButton onClick={() => fileUpload.setShowImageModal(false)}>
              <FaTimes />
            </CloseButton>
            {props.modo === TipoFormulario.tpVisualizacao && (
              <DownloadButton
                onClick={(e) => {
                  e.stopPropagation();
                  const filename = fileUpload.currentImage.split('/').pop() || 'imagem';
                  fileUpload.handleDownloadFile(fileUpload.currentImage, filename);
                }}
                title="Baixar imagem"
              >
                <FaDownload />
              </DownloadButton>
            )}
          </ImagePreviewContent>
        </ImagePreviewOverlay>
      )}

      {tutorialVideoUrl && (() => {
        const embedUrl = toYoutubeEmbedUrl(tutorialVideoUrl);
        return (
          <TutorialVideoOverlay onClick={() => setTutorialVideoUrl(null)}>
            <TutorialVideoContainer onClick={(e) => e.stopPropagation()}>
              <TutorialVideoHeader>
                <TutorialVideoTitle>
                  <HelpIcon />
                  Entenda como Funciona
                </TutorialVideoTitle>
                <TutorialVideoHeaderActions>
                  <TutorialVideoExternalLink
                    href={tutorialVideoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Abrir video em nova aba"
                  >
                    Assistir Externamente
                  </TutorialVideoExternalLink>
                  <TutorialVideoClose
                    type="button"
                    aria-label="Fechar"
                    title="Fechar"
                    onClick={() => setTutorialVideoUrl(null)}
                  >
                    <FaTimes />
                  </TutorialVideoClose>
                </TutorialVideoHeaderActions>
              </TutorialVideoHeader>
              <TutorialVideoBody>
                <TutorialVideoIframe
                  src={`${embedUrl}${embedUrl.includes('?') ? '&' : '?'}autoplay=1&rel=0`}
                  title="Tutorial"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </TutorialVideoBody>
            </TutorialVideoContainer>
          </TutorialVideoOverlay>
        );
      })()}
    </>
  );
};

export default React.memo(ModalForm);
